import React, { useMemo, useState } from "react";
import { useMedia } from "react-media";
import { useAuth } from "../../../contexts/Auth";
import BeautifulSkeleton from "../../common/Skeletons/BeautifulSkeleton";
import EventCard from "./EventCard";
import {
  DAY_LABELS,
  DAY_NAMES,
  LIVESTREAM_SCHEDULE,
  WEEKDAYS,
} from "./schedule";
import "./CalendarPage.scss";

const mediaQuery = {
  isMobile: "screen and (max-width: 767px)",
};

// Settings > Calendar: weekly livestream calendar filtered to the events the
// user's subscriptions grant. Desktop is a Mon–Fri five-column grid; below
// 768px a day-selector tab bar shows one day at a time.
function CalendarPage() {
  const {
    loading,
    hasWealthSeriesAtlas,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitan,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverest,
    hasWealthSeriesEverestAuto,
    hasWealthSeriesCore,
    hasWealthSeriesLite,
    canSeeInternal,
    hasOptionsAcademy,
    hasOptionsAcademyPlus,
    hasPlatinumProgram,
    hasEmerald,
  } = useAuth();
  const { isMobile } = useMedia({ queries: mediaQuery });
  const [selectedDay, setSelectedDay] = useState(1);

  // Keys are the schedule's requiredFlag strings. Auto-traded Wealth Series
  // variants unlock the same pages as the manual tiers (Sidebar does the same
  // OR), and Options Academy Plus includes Academy access. Unknown flags stay
  // undefined -> falsy, so an unmapped event is hidden, never shown.
  const activeFlags = useMemo(
    () => ({
      hasWealthSeriesAtlas: hasWealthSeriesAtlas || hasWealthSeriesAtlasAuto,
      hasWealthSeriesTitan: hasWealthSeriesTitan || hasWealthSeriesTitanAuto,
      hasWealthSeriesEverest:
        hasWealthSeriesEverest || hasWealthSeriesEverestAuto,
      hasWealthSeriesCore,
      hasWealthSeriesLite,
      canSeeInternal,
      hasOptionsAcademy: hasOptionsAcademy || hasOptionsAcademyPlus,
      hasPlatinumProgram,
      hasEmerald,
    }),
    [
      hasWealthSeriesAtlas,
      hasWealthSeriesAtlasAuto,
      hasWealthSeriesTitan,
      hasWealthSeriesTitanAuto,
      hasWealthSeriesEverest,
      hasWealthSeriesEverestAuto,
      hasWealthSeriesCore,
      hasWealthSeriesLite,
      canSeeInternal,
      hasOptionsAcademy,
      hasOptionsAcademyPlus,
      hasPlatinumProgram,
      hasEmerald,
    ],
  );

  const eventsByDay = useMemo(() => {
    const groups = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    LIVESTREAM_SCHEDULE.filter(
      (event) => activeFlags[event.requiredFlag],
    ).forEach((event) => {
      event.days.forEach((day) => groups[day].push(event));
    });
    WEEKDAYS.forEach((day) => {
      groups[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return groups;
  }, [activeFlags]);

  const renderDayEvents = (day) =>
    eventsByDay[day].map((event) => (
      // Multi-day events render once per column, so keys need the day.
      <EventCard key={`${event.id}-${day}`} event={event} />
    ));

  return (
    <div className="calendar-page">
      <h1 className="calendar-page__heading">Livestream Calendar</h1>
      <div className="calendar-page__subtitle">
        All times are Eastern (EST). Events shown are based on your subscription
        only.
      </div>

      {loading && (
        // While auth loads every flag is false — skeletons instead of a flash
        // of "No events".
        <div className="calendar-page__skeletons">
          <BeautifulSkeleton variant="rectangular" width="100%" height={72} />
          <BeautifulSkeleton variant="rectangular" width="100%" height={72} />
          <BeautifulSkeleton variant="rectangular" width="100%" height={72} />
          <BeautifulSkeleton variant="rectangular" width="100%" height={72} />
        </div>
      )}

      {!loading && !isMobile && (
        <div className="calendar-page__grid">
          {WEEKDAYS.map((day) => (
            <div key={day} className="calendar-page__day-column">
              <div className="calendar-page__day-header">{DAY_LABELS[day]}</div>
              {eventsByDay[day].length === 0 ? (
                <div className="calendar-page__empty">No events</div>
              ) : (
                renderDayEvents(day)
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && isMobile && (
        <React.Fragment>
          <div className="calendar-page__day-tabs">
            {WEEKDAYS.map((day) => (
              <button
                key={day}
                type="button"
                className={`calendar-page__day-tab${
                  selectedDay === day ? " calendar-page__day-tab--selected" : ""
                }`}
                onClick={() => setSelectedDay(day)}
              >
                {DAY_LABELS[day]}
              </button>
            ))}
          </div>
          <div className="calendar-page__day-list">
            {eventsByDay[selectedDay].length === 0 ? (
              <div className="calendar-page__empty calendar-page__empty--mobile">
                No events on {DAY_NAMES[selectedDay]}
              </div>
            ) : (
              renderDayEvents(selectedDay)
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export default CalendarPage;
